import { type CSSProperties, memo, type ReactNode } from 'react';

import { HelpPopover } from '@/shared/cms/question-mark';
import { Card } from '@/shared/ui/card';
import { TitleWithHelp } from '@/shared/ui/title-with-help';

import styles from './pattern-stats-table.module.css';

interface StatsRowProps {
    item: {
        label: ReactNode;
        value: ReactNode;
        helpId?: string | undefined;
        slug?: string | undefined;
    };
}

const StatsRow = memo(({ item }: StatsRowProps) => (
    <div className={styles.statsRow}>
        <span className={styles.label}>{item.label}:</span>
        <span className={styles.value}>{item.value}</span>
        <span className={styles.iconCell}>
            {item.helpId && <HelpPopover helpId={item.helpId} />}
        </span>
    </div>
));

StatsRow.displayName = 'StatsRow';

interface PatternStatsTableProps {
    title: string;
    data: Array<{
        label: ReactNode;
        value: ReactNode;
        helpId?: string | undefined;
        slug?: string | undefined;
    }>;
    accentColor?: string | undefined;
    helpId?: string;
    isFollowingCursor?: boolean;
}

export const PatternStatsTable = memo(
    ({
        title,
        data,
        accentColor,
        helpId,
        isFollowingCursor = false,
    }: PatternStatsTableProps) => (
        <Card
            accentColor={accentColor}
            className={styles.card}
            innerClassName={styles.cardInner}
            isFollowingCursor={isFollowingCursor}
            isFullHeight
            wrapperClassName={styles.cardWrapper}
        >
            <TitleWithHelp
                className={styles.titleWithHelpRight}
                helpId={helpId}
                title={title}
            />

            <div
                className={styles.separator}
                style={{ '--accent-color': accentColor } as CSSProperties}
            />

            <div className={styles.statsContainer}>
                {data.map((item) => (
                    <StatsRow
                        key={`${item.label}-${item.value}`}
                        item={item}
                    />
                ))}
            </div>
        </Card>
    ),
);

PatternStatsTable.displayName = 'PatternStatsTable';
